import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import { evaluatePassword } from "../lib/passwordPolicy";

/**
 * Fase 11.5 — Confirmar reset de contraseña.
 *
 * URL: /reset-password?token=...
 * - Lee token del query string.
 * - User ingresa nueva password 2 veces.
 * - POST /auth/password-reset/confirm.
 * - Si OK → redirige a login con toast.
 * - Si token inválido/expirado → mensaje claro.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token || token.length < 32) {
      setError("Enlace inválido o incompleto. Solicita uno nuevo desde 'Olvidé mi contraseña'.");
    }
  }, [token]);

  const pwdEval = evaluatePassword(pwd);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token || token.length < 32) {
      setError("Enlace inválido. Solicita uno nuevo.");
      return;
    }
    if (pwd.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (pwd !== pwd2) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pwd });
      setSuccess(true);
      toast.success("Contraseña actualizada");
      // Redirige a login tras un par de segundos.
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e) {
      const detail = formatApiError(e?.response?.data?.detail) || "No se pudo restablecer la contraseña.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="container-prose py-20">
        <div
          className="max-w-md mx-auto border border-emerald-200 bg-emerald-50/40 p-8 rounded-sm"
          data-testid="reset-password-success"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-800 font-medium mb-3">
            Listo
          </p>
          <h1 className="font-serif text-2xl text-gray-900 mb-3">Contraseña actualizada</h1>
          <p className="text-sm text-gray-800 mb-4">
            Tu contraseña fue restablecida correctamente. Te redirigimos para que inicies
            sesión con la nueva contraseña.
          </p>
          <Link to="/login" className="text-sm text-primary hover:underline">
            Iniciar sesión ahora →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-prose py-20">
      <div className="max-w-md mx-auto border border-gray-200 bg-white p-8">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-3">
            Restablecer contraseña
          </p>
          <h1 className="font-serif text-3xl text-gray-900 mb-2">Crea tu nueva contraseña</h1>
          <p className="text-sm text-gray-600">
            Elige una contraseña segura. Mínimo 8 caracteres. Te recomendamos combinar
            mayúsculas, minúsculas, números y un símbolo.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
          <div className="space-y-1.5">
            <Label htmlFor="new-password">Nueva contraseña</Label>
            <Input
              id="new-password"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              autoFocus
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="reset-password-new"
              className="rounded-sm h-11"
            />
            {pwd && <PasswordStrengthMeter value={pwd} testIdPrefix="reset-pwd" />}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirmar nueva contraseña</Label>
            <Input
              id="confirm-password"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              data-testid="reset-password-confirm"
              className="rounded-sm h-11"
            />
            {pwd2 && pwd !== pwd2 && (
              <p className="text-xs text-rose-700">Las contraseñas no coinciden.</p>
            )}
          </div>

          {error && (
            <div
              data-testid="reset-password-error"
              className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting || !token || !pwdEval.isValid || pwd !== pwd2}
            className="w-full rounded-sm h-11"
            data-testid="reset-password-submit"
          >
            {submitting ? "Guardando…" : "Restablecer contraseña"}
          </Button>
        </form>

        <p className="text-sm text-gray-600 mt-6 text-center">
          <Link to="/login" className="text-primary hover:underline">
            ← Volver a iniciar sesión
          </Link>
        </p>
      </div>
    </div>
  );
}
